import { ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { cn } from "@/core/utils/utils";
import type { IPayrollSheetApprovalAccessKey } from "@/payroll/payroll.d";
import { ApprovalMark } from "./approval-mark";
import { SheetApprovalPanel } from "./sheet-approval-panel";
import type { ApprovalChangeHandler, PendingApproval } from "./types";

interface SheetApprovalControlProps {
  accessKey: IPayrollSheetApprovalAccessKey;
  pendingApproval?: PendingApproval | null;
  /** z-index that clears the viewer's fullscreen overlay. */
  overlayClassName?: string;
  onApprovalChange?: ApprovalChangeHandler;
}

/**
 * Sign-off for the sheet you are on, in the workbook's own toolbar alongside the
 * other controls that act on the active sheet.
 *
 * Deliberately NOT in the sheet tab strip at the bottom: that strip has to keep
 * scrolling 40+ tabs, and its right end is where the floating support bubble
 * sits, so a control parked there gets crowded and half-covered. Up here it also
 * survives fullscreen, which is exactly when someone is reading the numbers they
 * are about to sign off.
 */
export const SheetApprovalControl = ({
  accessKey,
  pendingApproval,
  overlayClassName,
  onApprovalChange,
}: SheetApprovalControlProps) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={`Approvals for ${accessKey.name}`}
        // `group` so the chevron can follow the trigger's open state. Safe here,
        // unlike the header control: this button has a single Radix wrapper, so
        // nothing competes for `data-state`.
        className={cn(
          "group flex shrink-0 items-center gap-2 border-l border-stroke-tertiary px-3",
          "text-md text-tertiary-text transition-colors",
          "hover:bg-surface-secondary hover:text-secondary-text",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "data-[state=open]:bg-surface-secondary data-[state=open]:text-secondary-text",
        )}
      >
        <ApprovalMark accessKey={accessKey} size="md" />
        <span className="whitespace-nowrap">Approvals</span>
        <ChevronDown
          aria-hidden="true"
          className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
      </button>
    </PopoverTrigger>
    <PopoverContent
      side="bottom"
      align="end"
      className={cn("w-[22rem] p-0", overlayClassName)}
    >
      <SheetApprovalPanel
        accessKey={accessKey}
        pendingApproval={pendingApproval}
        onApprovalChange={onApprovalChange}
      />
    </PopoverContent>
  </Popover>
);
